// 表格列宽可拖动调整（#541，决策 D199）：拖表头右边缘改宽度，双击边缘恢复默认；调过的宽度按页面与表格记在浏览器里。
// 最后一列与分组表头不可拖：最后一列吸收剩余宽度。存储不可用（隐私模式等）时照常能拖，只是不记住。
import type { MouseEvent, PointerEvent } from "react";

/** 表头右边缘多宽的范围算拖动把手，与 ui.css 里 ax-table th::before 的宽度一致。 */
const EDGE = 6;
/** 拖动时一列最窄多少。 */
const MIN = 48;

/** 记宽度的键：路径里的数字 ID 归一（各详情页共用一份），再加上表格在页面里的序号（同一页的列表与弹窗里的表格分开）。 */
function storeKey(table: HTMLTableElement) {
  const path = location.pathname.replace(/\/\d+(?=\/|$)/g, "/:id");
  return `axon.colw.${path}.${[...document.querySelectorAll("table.ax-table")].indexOf(table)}`;
}

function load(key: string): Record<string, number> {
  try {
    const v: unknown = JSON.parse(localStorage.getItem(key) ?? "{}");
    return v && typeof v === "object" ? (v as Record<string, number>) : {};
  } catch {
    return {};
  }
}

function save(key: string, widths: Record<string, number>) {
  try {
    localStorage.setItem(key, JSON.stringify(widths));
  } catch {
    // 只影响下次打开时的宽度
  }
}

/** 列按表头文字记：显示列增减后序号会变，文字不变。 */
const label = (th: HTMLTableCellElement) => th.textContent?.trim() ?? "";

/** 可拖动的表头：不是本行最后一格（最后一列），不是分组表头。 */
function resizable(th: HTMLTableCellElement) {
  const row = th.parentElement as HTMLTableRowElement;
  return !th.classList.contains("ax-th-group") && th !== row.cells[row.cells.length - 1];
}

/** 承载宽度的元素：有 colgroup（分组表头）时是同一位置的 col，否则就是表头格。 */
function holder(table: HTMLTableElement, th: HTMLTableCellElement): HTMLElement {
  const cols = table.querySelectorAll<HTMLElement>(":scope > colgroup > col");
  if (!cols.length) return th;
  const left = th.getBoundingClientRect().left;
  const row = table.tBodies[0]?.rows[0] ?? (th.parentElement as HTMLTableRowElement);
  const i = [...row.cells].findIndex((c) => Math.abs(c.getBoundingClientRect().left - left) < 1);
  return cols[i] ?? th;
}

/** 设宽度；null 恢复成页面给的默认宽度（第一次改之前记在 data-w0 里）。 */
function setWidth(el: HTMLElement, px: number | null) {
  if (el.dataset.w0 === undefined) el.dataset.w0 = el.style.width;
  el.style.width = px === null ? el.dataset.w0 : `${px}px`;
}

/** 指针是否落在可拖动表头的右边缘，是则返回这个表头。 */
function edgeOf(e: { target: EventTarget; clientX: number }) {
  const th = (e.target as HTMLElement).closest?.("th");
  if (!th || !resizable(th)) return null;
  const r = th.getBoundingClientRect();
  return r.width > 0 && r.right - e.clientX <= EDGE ? th : null; // 宽度为 0 是还没排版（如 jsdom），不算边缘
}

/** 渲染后套用记住的宽度。 */
export function restoreWidths(table: HTMLTableElement) {
  const saved = load(storeKey(table));
  for (const row of table.tHead?.rows ?? []) {
    for (const th of row.cells) {
      const px = saved[label(th)];
      if (!px || !resizable(th)) continue;
      const el = holder(table, th);
      if (el.style.width !== `${px}px`) setWidth(el, px);
    }
  }
}

/** 拖动表头右边缘：跟着指针改宽度，松手记住；按在边缘之后的那次点击（拖动或双击恢复）不触发排序。after 在每次改宽后调用（重算最小宽度）。 */
export function startResize(e: PointerEvent<HTMLTableElement>, after: () => void) {
  const th = edgeOf(e);
  if (!th) return;
  e.preventDefault(); // 不选中文字
  const table = e.currentTarget;
  const el = holder(table, th);
  const start = e.clientX;
  const from = th.getBoundingClientRect().width;
  let moved = false;
  const move = (ev: globalThis.PointerEvent) => {
    moved = true;
    setWidth(el, Math.max(MIN, Math.round(from + ev.clientX - start)));
    after();
  };
  const swallow = (ev: Event) => { ev.stopPropagation(); ev.preventDefault(); };
  const up = () => {
    window.removeEventListener("pointermove", move);
    window.removeEventListener("pointerup", up);
    document.body.style.cursor = "";
    // 按在边缘后紧跟的 click 会落到表头上触发排序（拖动、双击恢复都是）：吞掉这一次，没有 click 跟来就在下一轮事件循环撤掉
    window.addEventListener("click", swallow, { capture: true, once: true });
    setTimeout(() => window.removeEventListener("click", swallow, { capture: true }));
    if (!moved) return;
    const key = storeKey(table);
    save(key, { ...load(key), [label(th)]: parseFloat(el.style.width) });
  };
  document.body.style.cursor = "col-resize";
  window.addEventListener("pointermove", move);
  window.addEventListener("pointerup", up);
}

/** 双击表头右边缘：恢复这一列的默认宽度，忘掉记住的值。 */
export function resetWidth(e: MouseEvent<HTMLTableElement>, after: () => void) {
  const th = edgeOf(e);
  if (!th) return;
  const table = e.currentTarget;
  setWidth(holder(table, th), null);
  const key = storeKey(table);
  const rest = load(key);
  delete rest[label(th)];
  save(key, rest);
  after();
}
